/*
 Push an object in the dataLayer for every promotion container on the homepage.
 Example format:

{
  'id': 'BELLES20 - BRIDESMAID',
  'name': 'Belles Are Ringing - Bridesmaid',
  'position': 'vp3-cr2-m1-btn1'
}
*/

const dataLayer = window.dataLayer || [];

export default () => {
  if (document.body.getAttribute('data-template') !== 'index') return;

  const customTriggers = document.querySelectorAll('[data-promo-trigger="custom"]');

  // Remove only data-tracking-id from children of custom-triggered parents
  // to prevent GTM's automatic select_promotion trigger on click.
  // Keep data-promo-tracking so children are still picked up for view_promotion.
  customTriggers.forEach((parent) => {
    parent.querySelectorAll('[data-tracking-id]').forEach((child) => {
      child.removeAttribute('data-tracking-id');
    });
  });

  // Fire select_promotion on click for custom-triggered elements
  // Only bind to the top-level custom trigger to avoid duplicate events from nesting
  customTriggers.forEach((el) => {
    if (el.parentElement?.closest('[data-promo-trigger="custom"]')) return;

    // Detect once whether this frame contains a product carousel — used below to
    // gate clicks on arrows, pagination, and empty space between tiles.
    const isProductCarouselFrame = !!el.querySelector('.product-rec-c-carousel, [data-product-tiles], .swiper-wrapper');

    el.addEventListener('click', (e) => {
      // For product carousel frames, only fire when the click lands inside a product tile.
      // Arrows, pagination dots, and empty space between tiles should not produce events.
      if (isProductCarouselFrame) {
        const insideProductTile = e.target.closest('.ProductTile, .product-rec-c-carousel__product-card, .fs_product');
        if (!insideProductTile) return;
      }

      // Find the closest child element with tracking attributes from the click target
      const closestWithCreative = e.target.closest('[data-promo-creative]');
      const closestWithTitle = e.target.closest('[data-promo-title]');

      // Determine if the click landed on a child element (not the parent trigger itself)
      const hasChildCreative = closestWithCreative && closestWithCreative !== el;
      const hasChildTitle = closestWithTitle && closestWithTitle !== el;

      let creativeName;
      let promotionName;

      if (hasChildCreative || hasChildTitle) {
        // Click landed on a child — use child's values, fall back to parent if missing
        creativeName = hasChildCreative
          ? closestWithCreative.getAttribute('data-promo-creative')
          : el.getAttribute('data-promo-creative') || null;
        promotionName = hasChildTitle
          ? closestWithTitle.getAttribute('data-promo-title')
          : el.getAttribute('data-promo-title') || null;
      } else {
        // Click landed on the parent itself — use parent's values
        creativeName = el.getAttribute('data-promo-creative') || null;
        promotionName = el.getAttribute('data-promo-title') || null;
      }

      // Only fire if we have both creative and CTA (TC07/TC08: skip if either is missing)
      if (!creativeName || !promotionName) return;

      const trackingId = el.getAttribute('data-tracking-id');
      const position = el.dataset.sectionTracking;

      setTimeout(() => {
        dataLayer.push({
          event: 'select_promotion',
          creative_name: creativeName,
          creative_slot: position,
          promotion_id: trackingId,
          promotion_name: promotionName,
          ecommerce: { items: [] },
        });
      }, 0);
    });
  });

  const promotions = [];
  const isDesktop = window.innerWidth > 767;
  const creative = isDesktop
    ? document.querySelectorAll('[data-promo-tracking], [data-promo-tracking-desktop]')
    : document.querySelectorAll('[data-promo-tracking], [data-promo-tracking-mobile]');

  let promoIndex = 0;
  const promotionTypeList = [];

  let vpNumber = 1;
  let ptNumber = 1;
  let mNumber = 1;

  creative.forEach((el, i) => {
    const trackingId = el.closest('[data-tracking-id]')?.getAttribute('data-tracking-id');
    const sectionId = el.closest('[data-section-id]')?.getAttribute('data-section-id');
    const prevSectionId = i > 0 ? creative[i - 1].closest('section, footer')?.getAttribute('data-section-id') : null;
    // Resolve CTA from the element itself, or walk up to find the nearest ancestor's CTA.
    // No textContent fallback — elements without an explicit data-promo-title should not
    // produce a tracking event with leaked DOM text.
    const promotionTitle =
      el.getAttribute('data-promo-title') || el.closest('[data-promo-title]')?.getAttribute('data-promo-title') || null;
    const creativeName = el.getAttribute('data-promo-creative')
      ? el.getAttribute('data-promo-creative')
      : el.closest('section, footer')?.getAttribute('data-section-type');
    let promotionName = promotionTitle;

    // container type (cr, sldr, hero, vdo, grid, cta)
    const containerTypeElement = el.closest('[data-container-type]');
    let ct = containerTypeElement ? containerTypeElement.getAttribute('data-container-type') : null;

    // promo type (btn, txt, link, img)
    const promoTypeElement = el.closest('[data-promo-type]');
    const pt = promoTypeElement ? promoTypeElement.getAttribute('data-promo-type') : null;

    let ctNumber = 1;

    promotionTypeList.push(pt);

    // Set containerType to carousel for mobile Pods
    if (el.closest('section, footer')?.getAttribute('data-section-type') === 'INF-Pods') {
      const heading = el.closest('section, footer')?.querySelector('.Pod__heading')?.textContent.trim();
      if (el.closest('section, footer')?.querySelector('.PodsCarousel') && !isDesktop) {
        el.closest('[data-container-type]')?.setAttribute('data-container-type', 'cr');
        ct = 'cr';

        if (prevSectionId === trackingId) {
          mNumber++;
        } else {
          mNumber = 1;
        }
      }

      // If Pod has headings (h1, h2, h3 etc), use heading as the promo name. Otherwise,
      // fall back to the creative name (no textContent fallback — it leaks unrelated DOM text).
      if (heading) {
        promotionName = heading;
      } else {
        promotionName = promotionName || creativeName;
      }
    }

    // count container type number
    const ctList = Array.from(creative).filter((item) => {
      const containerType = item.closest('[data-container-type]');
      return containerType && containerType.getAttribute('data-container-type') === ct;
    });

    const ctListIds = ctList
      .map((item) => {
        const sectionIdElement = item.closest('[data-section-id]');
        return sectionIdElement ? sectionIdElement.getAttribute('data-section-id') : null;
      })
      .filter((id) => id !== null);

    for (let index = 0; index < ctListIds.length; index++) {
      if (ctListIds[index] === sectionId) {
        break;
      } else if (ctListIds[index] !== ctListIds[index - 1]) {
        ctNumber++;
      }
    }

    // count promo type number
    if (promoIndex !== 0) {
      if (promotions[i - 1].creative === creativeName && promotionTypeList[i - 1] === pt) {
        ptNumber++;
      } else {
        ptNumber = 1;
      }

      // count view position number
      if (promotions[i - 1].creative !== creativeName) {
        vpNumber++;
      }
    }

    const concatPosition = `vp${vpNumber}-${ct + ctNumber}-m${mNumber}-${pt + ptNumber}`;

    const promo = {
      id: trackingId,
      creative: creativeName,
      name: promotionName,
      position: concatPosition,
    };

    el.dataset.sectionTracking = concatPosition;

    promotions.push(promo);
    promoIndex++;
  });

  // Track promotions when they come into viewport using IntersectionObserver
  const observerOptions = {
    root: null, // viewport
    rootMargin: '0px',
    threshold: 0.1, // 10% of the element must be visible to trigger
  };

  const trackedPromotions = new Set(); // Keep track of already-viewed promotions

  const observerCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const promotionName = element.dataset.promoTitle;
        // Find the promo data that matches this element
        const promoData = promotions.find((p) => p.name === promotionName);

        // Only fire view_promotion if we have both creative and name (TC07/TC08)
        if (promoData && !trackedPromotions.has(promotionName) && promoData.name && promoData.creative) {
          // Mark as tracked
          trackedPromotions.add(promotionName);

          // Push to dataLayer with GA4 event parameters
          dataLayer.push({
            event: 'view_promotion',
            creative_name: promoData.creative,
            creative_slot: promoData.position,
            promotion_id: promoData.id,
            promotion_name: promoData.name,
            ecommerce: { items: [] },
          });

          // Stop observing this element
          observer.unobserve(element);
        }
      }
    });
  };

  const observer = new IntersectionObserver(observerCallback, observerOptions);

  // Observe each element with promo tracking
  creative.forEach((el) => {
    if (el.dataset.sectionTracking) {
      observer.observe(el);
    }
  });
};
